import * as React from "react";
import { Alert, Box, CircularProgress, Grid, Typography } from "@mui/material";
import {
  extractVinalfaList,
  groupHash,
  groupImage,
  groupLabel,
  loadVinalfaVehicleContext,
  saveOemDiagramNav,
  VinalfaGroup,
} from "../../../interfaces/Vinalfa";
import {
  fetchVinalfaGroups,
  fetchVinalfaSectionChildren,
} from "../../../services/vinalfaService";
import VinalfaCatalogSidebar from "./VinalfaCatalogSidebar";
import VinalfaGraphicCards from "./VinalfaGraphicCards";
import VinalfaOemDiagramSection from "../../products/VinalfaOemDiagramSection";

const looksLikeDiagramLeaf = (item: VinalfaGroup): boolean => {
  if (item.hasDiagram === true) return true;
  if (item.hasChildren === false && groupImage(item)) return true;
  if (groupImage(item) && item.hasChildren !== true) return true;
  return false;
};

const isSectionFolder = (item: VinalfaGroup): boolean => {
  const h = groupHash(item);
  if (!h || h.startsWith("id:")) return false;
  if (groupImage(item) || looksLikeDiagramLeaf(item)) return false;
  return true;
};

export type SelectedOemSection = {
  hash: string;
  title: string;
  parentLabel?: string;
};

type Props = {
  /** When true, use denser full-width 3-col workspace (groups | diagram | parts) */
  workspaceLayout?: boolean;
  onOemClick?: (oem: string) => void;
};

const VinalfaDiagramPanel = ({
  workspaceLayout = false,
  onOemClick,
}: Props) => {
  const [vehicleHash, setVehicleHash] = React.useState<string | null>(null);
  const [linkageTargetId, setLinkageTargetId] = React.useState<string | null>(
    null
  );
  const [linkageTargetType, setLinkageTargetType] = React.useState<
    string | null
  >(null);

  const [groups, setGroups] = React.useState<VinalfaGroup[]>([]);
  const [selectedHash, setSelectedHash] = React.useState<string | null>(null);
  const [expandedHashes, setExpandedHashes] = React.useState<Set<string>>(
    () => new Set()
  );
  const [childrenByHash, setChildrenByHash] = React.useState<
    Record<string, VinalfaGroup[]>
  >({});
  const [cards, setCards] = React.useState<VinalfaGroup[]>([]);
  const [loadingRoot, setLoadingRoot] = React.useState(false);
  const [loadingHash, setLoadingHash] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedSection, setSelectedSection] =
    React.useState<SelectedOemSection | null>(null);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setVehicleHash(params.get("vehicleHash"));
    setLinkageTargetId(params.get("linkageTargetId"));
    setLinkageTargetType(params.get("linkageTargetType"));
  }, []);

  const seedEmbeddedChildren = React.useCallback((nodes: VinalfaGroup[]) => {
    const seeded: Record<string, VinalfaGroup[]> = {};
    const walk = (items: VinalfaGroup[]) => {
      for (const item of items) {
        const h = groupHash(item);
        if (h && Array.isArray(item.children) && item.children.length) {
          seeded[h] = item.children;
          walk(item.children);
        }
      }
    };
    walk(nodes);
    return seeded;
  }, []);

  const presentCards = React.useCallback(
    async (list: VinalfaGroup[], contextHash: string) => {
      const previewCount = list.filter((i) => groupImage(i)).length;
      const folders = list.filter(isSectionFolder);
      if (previewCount > 0 || folders.length === 0) {
        setCards(list);
        return list;
      }

      setLoadingHash(contextHash);
      try {
        const unitLists = await Promise.all(
          folders.map(async (folder) => {
            const h = groupHash(folder);
            const parentLabel = groupLabel(folder);
            try {
              const response = await fetchVinalfaSectionChildren(h);
              const units = extractVinalfaList(response).map((u) => ({
                ...u,
                parentLabel,
              }));
              setChildrenByHash((prev) => ({ ...prev, [h]: units }));
              return units;
            } catch {
              return [] as VinalfaGroup[];
            }
          })
        );
        const flat = unitLists.flat();
        const withImages = flat.filter(
          (u) => groupImage(u) || looksLikeDiagramLeaf(u)
        );
        const cardsToShow = withImages.length ? withImages : flat;
        setCards(cardsToShow);
        return cardsToShow;
      } finally {
        setLoadingHash(null);
      }
    },
    []
  );

  const loadChildren = React.useCallback(
    async (hash: string, embedded?: VinalfaGroup[]) => {
      if (hash.startsWith("id:") && embedded) {
        setChildrenByHash((prev) => ({ ...prev, [hash]: embedded }));
        await presentCards(embedded, hash);
        return embedded;
      }
      if (hash.startsWith("id:")) {
        return [];
      }
      if (embedded && embedded.length) {
        setChildrenByHash((prev) => ({ ...prev, [hash]: embedded }));
        await presentCards(embedded, hash);
        return embedded;
      }
      setLoadingHash(hash);
      try {
        const response = await fetchVinalfaSectionChildren(hash);
        const list = extractVinalfaList(response);
        setChildrenByHash((prev) => ({ ...prev, [hash]: list }));
        await presentCards(list, hash);
        return list;
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || "Failed to load OEM sections.";
        setError(String(detail));
        return [];
      } finally {
        setLoadingHash(null);
      }
    },
    [presentCards]
  );

  React.useEffect(() => {
    if (!vehicleHash) return;
    let cancelled = false;
    (async () => {
      setLoadingRoot(true);
      setError(null);
      try {
        const response = await fetchVinalfaGroups(vehicleHash);
        if (cancelled) return;
        const list = extractVinalfaList(response);
        const seeded = seedEmbeddedChildren(list);
        setGroups(list);
        if (Object.keys(seeded).length) {
          setChildrenByHash((prev) => ({ ...prev, ...seeded }));
        }
        if (list.length > 0) {
          const first = list[0];
          const hash = groupHash(first);
          const embedded = Array.isArray(first.children)
            ? first.children
            : seeded[hash];
          setSelectedHash(hash);
          setExpandedHashes(new Set([hash]));
          await loadChildren(hash, embedded);
        }
      } catch (err: any) {
        if (!cancelled) {
          const detail =
            err?.response?.data?.detail || "Failed to load OEM catalog groups.";
          setError(String(detail));
        }
      } finally {
        if (!cancelled) setLoadingRoot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleHash, loadChildren, seedEmbeddedChildren]);

  const onSelect = async (group: VinalfaGroup) => {
    const hash = groupHash(group);
    if (!hash) return;
    setSelectedHash(hash);
    setSelectedSection(null);
    setError(null);
    if (childrenByHash[hash]) {
      await presentCards(childrenByHash[hash], hash);
      return;
    }
    const embedded = Array.isArray(group.children) ? group.children : undefined;
    await loadChildren(hash, embedded);
  };

  const onToggleExpand = async (group: VinalfaGroup) => {
    const hash = groupHash(group);
    if (!hash) return;
    setExpandedHashes((prev) => {
      const next = new Set(prev);
      if (next.has(hash)) next.delete(hash);
      else next.add(hash);
      return next;
    });
    if (!childrenByHash[hash]) {
      const embedded = Array.isArray(group.children) ? group.children : undefined;
      await loadChildren(hash, embedded);
    }
  };

  const openSectionInPlace = (item: VinalfaGroup) => {
    const sectionHash = groupHash(item);
    if (!sectionHash) return;
    const title = groupLabel(item);
    const siblings = cards
      .map((c) => ({ hash: groupHash(c), title: groupLabel(c) }))
      .filter((s) => s.hash && !s.hash.startsWith("id:"));
    const currentIndex = Math.max(
      0,
      siblings.findIndex((s) => s.hash === sectionHash)
    );
    const parentLabel =
      (typeof item.parentLabel === "string" && item.parentLabel) ||
      (groups.find((g) => groupHash(g) === selectedHash)
        ? groupLabel(groups.find((g) => groupHash(g) === selectedHash)!)
        : undefined);
    const ctx = loadVinalfaVehicleContext();
    saveOemDiagramNav({
      vehicleLabel: ctx
        ? `${ctx.catalog || ""} ${ctx.model || ""}`.trim()
        : undefined,
      parentLabel,
      siblings:
        siblings.length > 0 ? siblings : [{ hash: sectionHash, title }],
      currentIndex,
    });
    setSelectedSection({ hash: sectionHash, title, parentLabel });
  };

  const onCardSelect = async (item: VinalfaGroup) => {
    const hash = groupHash(item);
    if (!hash) return;

    if (looksLikeDiagramLeaf(item)) {
      openSectionInPlace(item);
      return;
    }

    setSelectedHash(hash);
    setExpandedHashes((prev) => new Set(prev).add(hash));
    const kids = childrenByHash[hash] || (await loadChildren(hash));
    if (!kids.length || looksLikeDiagramLeaf(item)) {
      openSectionInPlace(item);
    }
  };

  const handleSectionChange = (nextHash: string, nextTitle: string) => {
    setSelectedSection((prev) => ({
      hash: nextHash,
      title: nextTitle,
      parentLabel: prev?.parentLabel,
    }));
  };

  if (!vehicleHash) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Identify a vehicle by VIN to browse OEM diagrams. Use Search by VIN on
        the home page, then open the Diagram tab.
      </Alert>
    );
  }

  if (loadingRoot) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sidebarMd = workspaceLayout ? 2.5 : 3.5;
  const contentMd = workspaceLayout ? 9.5 : 8.5;

  return (
    <Box sx={{ mt: workspaceLayout ? 0 : 1, width: "100%" }}>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={12} md={sidebarMd}>
          <VinalfaCatalogSidebar
            groups={groups}
            selectedHash={selectedHash}
            expandedHashes={expandedHashes}
            childrenByHash={childrenByHash}
            loadingHash={loadingHash}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
          />
        </Grid>
        <Grid item xs={12} md={contentMd}>
          {selectedSection ? (
            <VinalfaOemDiagramSection
              embedded
              sectionHash={selectedSection.hash}
              oemTitle={selectedSection.title}
              oemParent={selectedSection.parentLabel}
              vehicleHash={vehicleHash}
              linkageTargetId={linkageTargetId}
              linkageTargetType={linkageTargetType}
              onOemClick={onOemClick}
              onSectionChange={handleSectionChange}
              onBackToCards={() => setSelectedSection(null)}
            />
          ) : loadingHash && !cards.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 1.5, fontFamily: "Inter", fontWeight: 500 }}
              >
                Select a diagram
              </Typography>
              <VinalfaGraphicCards items={cards} onSelect={onCardSelect} />
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default VinalfaDiagramPanel;
